/**
 * The settings of a workspace that are one person's use of Airlock rather than
 * the team's (§5.25, owner 2026-11-02): how big their machine's sessions are,
 * how long they keep logs, whether their agent runs on its own. They never go
 * through the workspace's document. An organization that wants one everywhere
 * locks it in its own settings, which is a different door.
 *
 * Written as `null`, inheritance, rather than left out: `parse.ts` reads a
 * missing key as `null` anyway, and an older install's parser expects every key
 * of a configuration that is not sparse.
 *
 * A document published before this list holds such keys. They are read as `null`
 * everywhere, so they are no difference to publish and no change to pull, and
 * they leave for good with the next version published.
 */
import type { DocConfig, WorkspaceDocument } from './document';

export const PERSONAL_CONFIG_KEYS = [
  'defaultModel',
  'defaultEffort',
  'vmCpu',
  'vmMemoryMb',
  'vmDiskGb',
  'dockerDiskGb',
  'sandboxBackend',
  'containerRuntime',
  'archiveRetentionDays',
  'logRetentionMb',
  'logBufferLines',
  'idleHintMs',
  'notifications',
  'agentUpdate',
  'agentConfigSource',
  'autonomous',
  'autoSync'
] as const satisfies readonly (keyof DocConfig)[];

const PERSONAL: ReadonlySet<string> = new Set(PERSONAL_CONFIG_KEYS);

export const isPersonalKey = (key: string): boolean => PERSONAL.has(key);

/** The document with every personal setting at inheritance. */
export function withoutPersonal(document: WorkspaceDocument): WorkspaceDocument {
  const config = { ...document.config } as Record<string, unknown>;
  for (const key of PERSONAL_CONFIG_KEYS) config[key] = null;
  return { ...document, config: config as unknown as DocConfig };
}

/**
 * The document with the settings a member kept on this machine at what the last
 * version said, or absent where it said nothing: to the merge and the diff they
 * have not moved on either side, so they neither go out nor arrive.
 */
export function withKeptAtBase(
  document: WorkspaceDocument,
  base: WorkspaceDocument | null,
  kept: readonly string[]
): WorkspaceDocument {
  if (!kept.length) return document;
  const config = { ...document.config } as Record<string, unknown>;
  const said = (base?.config ?? {}) as Record<string, unknown>;
  for (const key of kept) {
    if (key in said) config[key] = said[key];
    else delete config[key];
  }
  return { ...document, config: config as unknown as DocConfig };
}

/** A pulled configuration as a patch of this copy's: nothing a member holds as their own is in it. */
export function patchOfTeamSettings<T extends object>(patch: T, kept: readonly string[]): T {
  const out = { ...patch } as Record<string, unknown>;
  for (const key of PERSONAL_CONFIG_KEYS) delete out[key];
  for (const key of kept) delete out[key];
  return out as T;
}
